import type { ComboBoxOption } from 'components/ComboBox'
import { formatDeliveryReportTenantName } from 'repositories/ShipmentApi/getDeliveryReportTenant/formatTenantName'
import type { DeliveryTenant } from 'repositories/ShipmentApi/getDeliveryReportTenant/type'
import type {
  ShipmentVisitDetail,
  DeliveryWork,
  DeliveryWorkAttachment,
} from 'repositories/ShipmentApi/getShipment/type'

// ComboBox のプレースホルダ
export const DELIVERY_DESTINATION_PLACEHOLDER = '納品先入力してください'

// 手入力履歴の ComboBox 選択肢 value に付ける接頭辞。
// マスタ選択肢（value = tenant_id の数値文字列）と区別し、選択時に手入力扱いへ復元するために使う。
export const LOCAL_DELIVERY_HISTORY_VALUE_PREFIX = 'manual-history:'

export type VisitStamps = {
  checkin?: string
  start?: string
  end?: string
  checkout?: string
}

// 確定済み納品先の入力種別。マスタ選択 / 手入力 / 未登録
export type SavedDeliverySource = 'master' | 'manual' | null

export type Visit = {
  id: string
  // DB 永続済み visit の数値 ID。全 visit が API 由来になったため常に数値
  dbVisitId: number | null
  // ComboBox（編集中ドラフト）の選択値・表示文字列。読み込み時は常に空にし、
  // 確定済みの値は savedDeliveryLabel として下部にテキスト表示する（プリフィルしない）
  deliveryTenantId: string | null
  deliveryTenantLabel: string
  // サーバに登録済み（確定済み）の納品先表示名と入力種別。ComboBox の編集中ドラフトとは独立し、
  // 「決定」保存 → getShipment 再取得時にのみ更新される。未登録なら空文字 / null
  savedDeliveryLabel: string
  savedDeliverySource: SavedDeliverySource
  stamps: VisitStamps
  deliveryWork: DeliveryWork | null
  deliveryWorkAttachments: DeliveryWorkAttachment[]
}

// 出荷元テナント取得 API の delivery_tenants を ComboBox の選択肢に変換する。
// value は tenant_id（文字列）、label は会社+倉庫+テナント名（既存の表示整形を再利用）。
export const toDeliveryDestinationOptions = (deliveryTenants: DeliveryTenant[]): ComboBoxOption[] =>
  deliveryTenants.map((dt) => ({
    label: formatDeliveryReportTenantName(dt),
    value: String(dt.tenantId),
  }))

// 手入力履歴（納品先名の配列）を ComboBox の選択肢に変換する。
// value は接頭辞付きにしてマスタ tenant_id と衝突させない（label はそのまま納品先名）。
// masterLabels に完全一致する履歴は除外する（候補で同名が「履歴」と「マスタ」に
// 二重表示されるのを防ぐ。マスタ側に出るので履歴からは落とす）。
export const toManualDeliveryHistoryOptions = (
  names: string[],
  masterLabels: ReadonlySet<string> = new Set()
): ComboBoxOption[] =>
  names
    .filter((name) => !masterLabels.has(name))
    .map((name) => ({
      label: name,
      value: `${LOCAL_DELIVERY_HISTORY_VALUE_PREFIX}${name}`,
    }))

// ComboBox の選択値が手入力履歴由来か判定する。value 形式の知識をこのモジュールに閉じ込め、
// 利用側（controller）が接頭辞の詳細を知らなくて済むようにする。
export const isManualHistoryOptionValue = (value: string | null): boolean =>
  value !== null && value.startsWith(LOCAL_DELIVERY_HISTORY_VALUE_PREFIX)

export const toStamps = (v: ShipmentVisitDetail): VisitStamps => ({
  checkin: v.checkinTime ?? undefined,
  start: v.startTime ?? undefined,
  end: v.endTime ?? undefined,
  checkout: v.checkoutTime ?? undefined,
})

// shipment 取得 API の visit を画面の Visit に変換する。
// 納品先ラベルは、マスタ選択（deliveryTenantId あり）なら選択肢から表示名を解決し、
// 手入力（deliveryDestination）ならその文字列をそのまま使う。
export const toVisit = (v: ShipmentVisitDetail, options: ComboBoxOption[]): Visit => {
  const tenantIdStr = v.deliveryTenantId !== null ? String(v.deliveryTenantId) : null
  // 確定済みの表示名と入力種別を解決する（マスタ選択なら選択肢のラベル、手入力ならその文字列）
  const savedDeliveryLabel =
    tenantIdStr !== null
      ? (options.find((o) => o.value === tenantIdStr)?.label ?? '')
      : (v.deliveryDestination ?? '')
  const savedDeliverySource: SavedDeliverySource =
    tenantIdStr !== null ? 'master' : v.deliveryDestination ? 'manual' : null
  return {
    id: String(v.id),
    dbVisitId: v.id,
    // ComboBox ドラフトは空で初期化（確定値はプリフィルせず下部テキストで見せる）
    deliveryTenantId: null,
    deliveryTenantLabel: '',
    savedDeliveryLabel,
    savedDeliverySource,
    stamps: toStamps(v),
    deliveryWork: v.deliveryWork,
    deliveryWorkAttachments: v.deliveryWorkAttachments ?? [],
  }
}

// 保存した visit の作業記録だけを最新化し、他 visit はオブジェクト参照ごと据え置く。
// 全 visit を作り直さないことで、編集途中の他 visit のローカルドラフトを保護する。
export const mergeSavedVisit = (
  visits: Visit[],
  savedVisitId: number,
  deliveryWork: DeliveryWork | null,
  deliveryWorkAttachments: DeliveryWorkAttachment[]
): Visit[] =>
  visits.map((v) =>
    v.dbVisitId === savedVisitId ? { ...v, deliveryWork, deliveryWorkAttachments } : v
  )
